// ============================================================================
// BEOFLOW BUTTON COMPONENT
// Synced with iOS BeoflowButton.swift
// ============================================================================

import { Button } from '@mui/material'
import type { ButtonProps } from '@mui/material'

export type BeoflowButtonVariant = 'primary' | 'secondary' | 'light' | 'dark' | 'outline' | 'outlineSecondary' | 'text' | 'textPrimary' | 'textSecondary'

interface BeoflowButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: BeoflowButtonVariant
}

/**
 * BeoflowButton - Standardized button component matching iOS design system
 *
 * Variants:
 * - primary: White glass (main CTAs)
 * - secondary: Mean blue glass (secondary actions)
 * - light: Subtle white tint (tertiary on dark backgrounds)
 * - dark: Subtle dark tint (for light backgrounds)
 * - outline: White glass outline
 * - outlineSecondary: Mean blue glass outline
 * - text: Text with underline (default white)
 * - textPrimary: Text with underline (mean blue)
 * - textSecondary: Text with underline (peak orange)
 *
 * @example
 * <BeoflowButton variant="primary">START WORKOUT</BeoflowButton>
 * <BeoflowButton variant="secondary">VIEW DETAILS</BeoflowButton>
 * <BeoflowButton variant="outline">CANCEL</BeoflowButton>
 */
export function BeoflowButton({ variant = 'primary', children, ...props }: BeoflowButtonProps) {
  const getMuiVariant = (): ButtonProps['variant'] => {
    switch (variant) {
      case 'primary':
        return 'contained'
      case 'secondary':
        return 'glassSecondary'
      case 'light':
        return 'glassLight'
      case 'dark':
        return 'glassDark'
      case 'outline':
        return 'glassOutline'
      case 'outlineSecondary':
        return 'glassOutlineSecondary'
      case 'text':
      case 'textPrimary':
      case 'textSecondary':
        return 'text'
      default:
        return 'contained'
    }
  }

  const getColorProp = (): ButtonProps['color'] => {
    if (variant === 'textPrimary') return 'primary'
    if (variant === 'textSecondary') return 'secondary'
    return undefined
  }

  const { sx, ...restProps } = props

  return (
    <Button
      variant={getMuiVariant()}
      color={getColorProp()}
      sx={[
        {
          touchAction: 'manipulation',
          WebkitTapHighlightColor: 'transparent',
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
      {...restProps}
    >
      {children}
    </Button>
  )
}
